import React, { useState, useEffect } from 'react';
import { FacebookAuthButtonProps } from '@/types/ui';
import { useNotifications } from '@/contexts/NotificationContext';
import axios from 'axios';
import { logger } from '../../utils/logger';

// Declaração do SDK do Facebook
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { accessToken: string } }) => void,
        options: { scope: string }
      ) => void;
      getLoginStatus: (callback: (response: { status: string; authResponse?: { accessToken: string } }) => void) => void;
    };
    fbAsyncInit: () => void;
  }
}

const FacebookAuthButton: React.FC<FacebookAuthButtonProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const { addNotification } = useNotifications();

  const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;

  useEffect(() => {
    // Se não tem App ID configurado, não carregar o SDK
    if (!FACEBOOK_APP_ID) {
      logger.warn('VITE_FACEBOOK_APP_ID não configurado', 'FacebookAuthButton');
      return;
    }

    // Evitar carregar múltiplas vezes
    if (document.getElementById('facebook-jssdk')) {
      if (window.FB) {
        requestAnimationFrame(() => setSdkLoaded(true));
      }
      return;
    }

    // Callback quando SDK carregar (padrão oficial Facebook)
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
      setSdkLoaded(true);
    };

    // Carregar script do Facebook (padrão oficial)
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.async = true;
    script.defer = true;
    
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.body.appendChild(script);
    }
  }, [FACEBOOK_APP_ID]);

  const handleFacebookLogin = () => {
    if (!window.FB) {
      addNotification({
        type: 'error',
        title: 'Erro',
        message: 'Facebook SDK não carregado. Tente novamente.',
      });
      return;
    }

    setLoading(true);

    window.FB.login(
      async (response) => {
        if (response.authResponse) {
          try {
            const accessToken = response.authResponse.accessToken;
            
            // Enviar para o backend validar — uses centralized API config
            const { API_BASE_URL } = await import('../../utils/apiConfig');
            
            const backendResponse = await axios.post(`${API_BASE_URL}/auth/social/facebook`, {
              accessToken,
            }, {
              withCredentials: true, // Include cookies
            });

            // ✅ Backend handles cookie setup - no need to store tokens
            // Tokens are in httpOnly cookies, not accessible from JS
            
            addNotification({
              type: 'success',
              title: 'Login com Facebook',
              message: `Bem-vindo, ${backendResponse.data.user?.name || 'Usuário'}!`,
            });

            if (onSuccess) onSuccess(backendResponse.data);
            
            // Aguardar um pouco para o AuthContext detectar o novo token antes de redirecionar
            setTimeout(() => {
              if (backendResponse.data.shouldCompleteProfile) {
                window.location.href = '/completar-perfil';
              } else {
                window.location.href = '/cliente/painel';
              }
            }, 500);
          } catch (error) {
            logger.error('Erro no login Facebook:', 'FacebookAuthButton', error);
            addNotification({
              type: 'error',
              title: 'Erro de Autenticação',
              // O motivo da recusa vem em `error` (formato do globalErrorHandler); ver GoogleAuthButton.
              message: axios.isAxiosError(error) && (error.response?.data?.message || error.response?.data?.error)
                ? error.response.data.message || error.response.data.error
                : 'Falha ao conectar com Facebook.',
            });
          }
        } else {
          addNotification({
            type: 'info',
            title: 'Cancelado',
            message: 'Login com Facebook cancelado.',
          });
        }
        setLoading(false);
      },
      { scope: 'email,public_profile' }
    );
  };

  // Se não tem App ID, não mostrar o botão
  if (!FACEBOOK_APP_ID) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleFacebookLogin}
      disabled={loading || !sdkLoaded}
      className="w-full flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5] text-white py-2 px-4 rounded font-medium transition-colors shadow-sm disabled:opacity-50"
    >
      {loading ? (
        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
      ) : (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )}
      Entrar com Facebook
    </button>
  );
};

export default FacebookAuthButton;
